/**
 * Whole-House Heat Loss Calculation Engine
 *
 * Implements BS EN 12831-compliant simplified heat loss calculations for UK
 * residential properties, following CIBSE Guide A design temperatures.
 *
 * Usage:
 *   import { calculateWholeHouseHeatLoss } from '@/domain/heatLoss/calculateHeatLoss';
 *
 * The engine is pure (no side effects) and reusable across:
 *   - Heat pump module
 *   - Radiator sizing module
 *   - LCOO engine
 *   - EPC alignment
 */

import type {
  Room,
  FabricElement,
  HeatLossResult,
  HeatLossSuitabilityBand,
  HeatLossSummary,
  SimpleHeatLossInputs,
  SimpleHeatLossResult,
} from './types';
import { deriveHLC, deriveACH, deriveExposedFactor } from './defaults';
import { calculateSimpleConfidence } from './confidence';

// ─── Physical Constants ─────────────────────────────────────────────────────

/**
 * Specific heat capacity of air: W per m³ per K per air change per hour.
 * From CIBSE Guide A.
 */
const AIR_HEAT_CAPACITY = 0.33;

// ─── Design Temperature Defaults ───────────────────────────────────────────

/** UK default internal design temperature (°C) */
const DEFAULT_INTERNAL_TEMP = 21;

/** UK default external design temperature (°C) — CIBSE Guide A cold spell */
const DEFAULT_EXTERNAL_TEMP = -3;

// ─── Room-Level Calculation ─────────────────────────────────────────────────

/**
 * Calculate heat loss for a single room.
 *
 * @param room   - Room geometry and fabric data
 * @param deltaT - Temperature differential (internal − external) in K
 * @returns Transmission loss (W), ventilation loss (W), and total (W)
 */
export function calculateRoomHeatLoss(
  room: Room,
  deltaT: number
): { transmission: number; ventilation: number; total: number } {
  const transmission = room.fabric.reduce((sum: number, element: FabricElement) => {
    return sum + element.areaM2 * element.uValue * deltaT;
  }, 0);

  const ventilation =
    room.volumeM3 * room.airChangesPerHour * AIR_HEAT_CAPACITY * deltaT;

  return {
    transmission,
    ventilation,
    total: transmission + ventilation,
  };
}

// ─── Confidence Scoring ─────────────────────────────────────────────────────

/**
 * Calculate a data-completeness confidence score (0–100) for a set of rooms.
 *
 * Scoring rules (cap at 100):
 *   +20  all rooms have volume defined (volumeM3 > 0)
 *   +20  all rooms have ACH defined (airChangesPerHour > 0)
 *   +30  all fabric elements carry a U-value
 *   +10  at least one window element is present
 *   +10  age band used to auto-populate U-value defaults (source = 'age_band')
 *   +10  at least one manual U-value override is present (source = 'manual')
 *
 * If the room array is empty, confidence is 0.
 */
export function calculateConfidence(rooms: Room[]): number {
  if (rooms.length === 0) return 0;

  let score = 0;

  // +20: all rooms have volume
  const allHaveVolume = rooms.every((r) => r.volumeM3 > 0);
  if (allHaveVolume) score += 20;

  // +20: all rooms have ACH
  const allHaveAch = rooms.every((r) => r.airChangesPerHour > 0);
  if (allHaveAch) score += 20;

  // +30: all fabric elements have U-values
  const allFabric = rooms.flatMap((r) => r.fabric);
  const allHaveUValues = allFabric.length > 0 && allFabric.every((f) => f.uValue > 0);
  if (allHaveUValues) score += 30;

  // +10: window area captured
  const hasWindows = allFabric.some((f) => f.type === 'window');
  if (hasWindows) score += 10;

  // +10: age-band defaults applied
  const hasAgeBandDefaults = allFabric.some((f) => f.uValueSource === 'age_band');
  if (hasAgeBandDefaults) score += 10;

  // +10: manual overrides applied
  const hasManualOverrides = allFabric.some((f) => f.uValueSource === 'manual');
  if (hasManualOverrides) score += 10;

  return Math.min(100, score);
}

// ─── Whole-House Calculation ────────────────────────────────────────────────

/**
 * Calculate whole-house heat loss from room-by-room data.
 *
 * @param rooms          - Array of rooms with geometry and fabric data
 * @param internalTemp   - Internal design temperature (°C, default 21)
 * @param externalTemp   - External design temperature (°C, default -3)
 * @returns HeatLossResult with totals, split, and confidence score
 */
export function calculateWholeHouseHeatLoss(
  rooms: Room[],
  internalTemp: number = DEFAULT_INTERNAL_TEMP,
  externalTemp: number = DEFAULT_EXTERNAL_TEMP
): HeatLossResult {
  const deltaT = internalTemp - externalTemp;

  let transmissionLossW = 0;
  let ventilationLossW = 0;

  for (const room of rooms) {
    const result = calculateRoomHeatLoss(room, deltaT);
    transmissionLossW += result.transmission;
    ventilationLossW += result.ventilation;
  }

  const totalHeatLossW = transmissionLossW + ventilationLossW;

  return {
    designTempInternal: internalTemp,
    designTempExternal: externalTemp,
    deltaT,
    transmissionLossW,
    ventilationLossW,
    totalHeatLossW,
    totalHeatLossKW: totalHeatLossW / 1000,
    confidence: calculateConfidence(rooms),
  };
}

// ─── Suitability Band ───────────────────────────────────────────────────────

/**
 * Derive the suitability band from a total heat loss in kW.
 */
export function getHeatLossSuitabilityBand(
  totalHeatLossKW: number
): HeatLossSuitabilityBand {
  if (totalHeatLossKW <= 8) return 'low_temp_suitable';
  if (totalHeatLossKW <= 14) return 'standard_boiler';
  return 'higher_capacity';
}

const SUITABILITY_INTERPRETATIONS: Record<HeatLossSuitabilityBand, string> = {
  low_temp_suitable: 'Suitable for low temperature systems',
  standard_boiler: 'Standard modern boiler range',
  higher_capacity: 'Higher capacity property — careful emitter review required',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Round a value to one decimal place. */
function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

// ─── Summary Builder ────────────────────────────────────────────────────────

/**
 * Build a human-readable HeatLossSummary from a HeatLossResult.
 *
 * Used by the presentation layer (heat loss summary card in the decision pack).
 */
export function buildHeatLossSummary(result: HeatLossResult): HeatLossSummary {
  const suitabilityBand = getHeatLossSuitabilityBand(result.totalHeatLossKW);

  return {
    designTempInternal: result.designTempInternal,
    designTempExternal: result.designTempExternal,
    totalHeatLossKW: roundToOneDecimal(result.totalHeatLossKW),
    transmissionLossKW: roundToOneDecimal(result.transmissionLossW / 1000),
    ventilationLossKW: roundToOneDecimal(result.ventilationLossW / 1000),
    confidence: result.confidence,
    suitabilityBand,
    interpretation: SUITABILITY_INTERPRETATIONS[suitabilityBand],
    lowConfidenceFlag: result.confidence < 60,
  };
}

// ─── Simple Whole-House Calculation ─────────────────────────────────────────

/** Default headroom fraction added to heat loss when deriving sizing target */
const DEFAULT_HEADROOM = 0.20;

/**
 * Calculate whole-house heat loss from the simple (non-room-by-room) input model.
 *
 * Formula:
 *   volume      = floorAreaM2 × ceilingHeightM  (+ extension volume when planned)
 *   deltaT      = internalDesignTempC − externalDesignTempC
 *   HLC         = derived from age band, insulation level, glazing type
 *   transmission = floorAreaM2 × HLC × deltaT × exposedFactor
 *   ventilation  = volume × ACH × 0.33 × deltaT
 *   total        = transmission + ventilation
 *   sizingTarget = total × (1 + headroom)
 *
 * @param inputs  - Simple whole-house inputs
 * @param headroomFraction - Sizing headroom fraction (default 0.20 = 20%)
 * @returns SimpleHeatLossResult
 */
export function calculateSimpleWholeHouseHeatLoss(
  inputs: SimpleHeatLossInputs,
  headroomFraction: number = DEFAULT_HEADROOM
): SimpleHeatLossResult {
  const notes: string[] = [];

  const {
    internalDesignTempC,
    externalDesignTempC,
    floorAreaM2,
    ceilingHeightM,
    propertyAgeBand,
    insulationLevel,
    insulationLevelExplicit,
    glazingType,
    glazingTypeExplicit,
    airTightness,
    airTightnessExplicit,
    exposedSides,
    exposedSidesExplicit,
    extensionPlanned,
    extensionAreaM2,
  } = inputs;

  const deltaT = internalDesignTempC - externalDesignTempC;

  // Build effective floor area (include extension when planned)
  let effectiveAreaM2 = floorAreaM2;
  if (extensionPlanned && extensionAreaM2 != null && extensionAreaM2 > 0) {
    effectiveAreaM2 += extensionAreaM2;
    notes.push(`Extension of ${extensionAreaM2} m² included in floor area.`);
  }

  const volumeM3 = effectiveAreaM2 * ceilingHeightM;

  if (ceilingHeightM === 2.4) {
    notes.push('Ceiling height assumed 2.4 m (default).');
  }

  // Derive HLC
  const hlc = deriveHLC(propertyAgeBand, insulationLevel, glazingType);
  if (!insulationLevelExplicit) {
    notes.push(`Insulation level inferred from property age band (${propertyAgeBand}) as "${insulationLevel}".`);
  }
  if (!glazingTypeExplicit) {
    notes.push(`Glazing type inferred from property age band (${propertyAgeBand}) as "${glazingType}".`);
  }

  // Derive exposed factor
  const exposedFactor = deriveExposedFactor(exposedSides);
  if (!exposedSidesExplicit) {
    notes.push(`Exposed sides inferred as ${exposedSides}.`);
  }

  // Transmission loss
  const transmissionLossW = effectiveAreaM2 * hlc * deltaT * exposedFactor;

  // Derive ACH
  const ach = deriveACH(airTightness, propertyAgeBand);
  if (!airTightnessExplicit) {
    notes.push(`Air tightness inferred from age band (${propertyAgeBand}) as "${airTightness}" (ACH ${ach.toFixed(2)}).`);
  } else {
    notes.push(`ACH ${ach.toFixed(2)} based on "${airTightness}" classification.`);
  }

  // Ventilation loss (CIBSE simplified method)
  const ventilationLossW = volumeM3 * ach * AIR_HEAT_CAPACITY * deltaT;

  const totalHeatLossW = transmissionLossW + ventilationLossW;
  const totalHeatLossKW = totalHeatLossW / 1000;

  const sizingTargetKW = totalHeatLossKW * (1 + headroomFraction);

  const confidence0to100 = calculateSimpleConfidence(inputs);

  notes.push(
    `Design temperatures: ${internalDesignTempC}°C internal / ${externalDesignTempC}°C external (ΔT = ${deltaT} K).`
  );

  return {
    designTempInternalC: internalDesignTempC,
    designTempExternalC: externalDesignTempC,
    deltaT,
    transmissionLossW,
    ventilationLossW,
    totalHeatLossW,
    totalHeatLossKW,
    headroomRecommendedPct: headroomFraction,
    sizingTargetKW,
    confidence0to100,
    notes,
  };
}
